import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Users, GraduationCap, School as SchoolIcon, Globe2, MapPin,
  TrendingUp, CheckCircle2, AlertTriangle, Download, Maximize2,
  Navigation, Phone, Building2, Layers, ChevronRight, Activity,
  Droplet, Zap, Wifi, ShieldCheck, Wrench,
} from "lucide-react";
import type { Catchment, CatchmentSchool, FinancialLayer } from "./types";
import {
  getCatchmentFinance, getSchoolFinance, getCatchmentHealthScore,
  getDataQualityScore, getInfrastructureScore, getMonthlyTrend,
  fmtUSD, fmtNum, fmtPct, fmtArea,
} from "./finance";
import { SCHOOL_LEVEL_COLORS } from "./markers";

const C = {
  forest: "#14532D",
  forestDeep: "#0C3320",
  forestMid: "#1E7A45",
  forestTint: "#E8F1EA",
  terracotta: "#B33A1A",
  mustard: "#C4A882",
  sky: "#0284c7",
  amber: "#d97706",
  emerald: "#059669",
  rose: "#e11d48",
  text: "#1a2e23",
  textMuted: "#5a7a6a",
  textLight: "#8aaa9a",
  bg: "#F6FAF8",
  card: "#FFFFFF",
  border: "#E0EDE7",
};

const OWNERSHIP_LABELS: Record<string, string> = {
  government: "Government", private: "Private", community: "Community", other: "Other",
};

type TabKey = "overview" | "schools" | "finance" | "infrastructure" | "coverage";

// ── Collection Ring ──────────────────────────────────────────────────────────
function CollectionRing({ rate, size = 120 }: { rate: number; size?: number }) {
  const stroke = 8;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - rate);
  const color = rate > 0.8 ? C.emerald : rate > 0.6 ? C.sky : rate > 0.4 ? C.amber : C.rose;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={C.border} strokeWidth={stroke} />
        <motion.circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circ}
          initial={{ strokeDashoffset: circ }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1, ease: "easeOut" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold" style={{ color: C.text }}>{fmtPct(rate)}</span>
        <span className="text-[10px] uppercase tracking-widest" style={{ color: C.textMuted }}>Collected</span>
      </div>
    </div>
  );
}

// ── Mini bar chart ───────────────────────────────────────────────────────────
function TrendChart({ data, mode }: { data: { month: string; billed: number; collected: number; outstanding: number; breccs: number }[]; mode: string }) {
  const max = Math.max(...data.map(d => Math.max(d.billed, d.collected, d.outstanding, d.breccs)));
  const color = mode === "billed" ? C.forest : mode === "collected" ? C.emerald : mode === "outstanding" ? C.rose : C.sky;
  return (
    <div className="flex items-end justify-between gap-1 h-32">
      {data.map((d, i) => {
        const val = (d as any)[mode] as number;
        const h = max > 0 ? (val / max) * 100 : 0;
        return (
          <div key={i} className="flex flex-col items-center gap-1 flex-1">
            <div className="w-full rounded-t-sm transition-all" style={{ height: `${h}%`, background: color, minHeight: 2, opacity: 0.85 }} />
            <span className="text-[9px]" style={{ color: C.textLight }}>{d.month}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── KPI Card ──────────────────────────────────────────────────────────────────
function KpiCard({ icon: Icon, label, value, sub, accent }: { icon: any; label: string; value: string; sub?: string; accent: string }) {
  return (
    <div className="rounded-xl border bg-white p-3" style={{ borderColor: C.border }}>
      <div className="flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg" style={{ background: `${accent}12`, color: accent }}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: C.textMuted, opacity: 0.7 }}>{label}</span>
      </div>
      <p className="mt-1.5 text-lg font-bold leading-tight" style={{ color: C.text }}>{value}</p>
      {sub && <p className="text-[10px]" style={{ color: C.textMuted }}>{sub}</p>}
    </div>
  );
}

// ── School Row ───────────────────────────────────────────────────────────────
function SchoolRow({ school, finance, selected, hovered, onSelect, onHover }: {
  school: CatchmentSchool; finance: ReturnType<typeof getSchoolFinance>;
  selected: boolean; hovered: boolean;
  onSelect: () => void; onHover: (id: string | null) => void;
}) {
  const levelColor = SCHOOL_LEVEL_COLORS[school.education_level ?? "other"] ?? C.forest;
  return (
    <motion.button
      onClick={onSelect}
      onMouseEnter={() => onHover(school.id)}
      onMouseLeave={() => onHover(null)}
      className="w-full rounded-xl border p-3 text-left transition-all"
      style={{
        borderColor: selected ? levelColor : hovered ? `${levelColor}60` : C.border,
        background: selected ? `${levelColor}08` : "white",
        boxShadow: selected ? `0 2px 8px ${levelColor}20` : "none",
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg" style={{ background: `${levelColor}15` }}>
            <SchoolIcon className="h-3.5 w-3.5" style={{ color: levelColor }} />
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold" style={{ color: C.text }}>{school.name}</p>
            <p className="text-[10px]" style={{ color: C.textMuted }}>
              {OWNERSHIP_LABELS[school.ownership ?? "other"] ?? "—"} · {school.education_level ?? "school"} · {school.boarding_type ?? "—"}
            </p>
          </div>
        </div>
        {school.location_status === "verified" && (
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" style={{ color: C.emerald }} />
        )}
      </div>
      <div className="mt-2 flex items-center justify-between text-[11px]">
        <span style={{ color: C.textMuted }}>{fmtNum(school.student_count)} students</span>
        <span className="font-semibold" style={{ color: finance.collectionRate > 0.8 ? C.emerald : finance.collectionRate > 0.6 ? C.sky : C.amber }}>
          {fmtPct(finance.collectionRate)}
        </span>
        <span className="font-medium" style={{ color: C.forest }}>{fmtUSD(finance.annualFees)}</span>
      </div>
    </motion.button>
  );
}

// ── Main Catchment Modal ─────────────────────────────────────────────────────
export function CatchmentModal({
  catchment, schools, onClose, onSelectSchool, financialLayer,
}: {
  catchment: Catchment;
  schools: CatchmentSchool[];
  onClose: () => void;
  onSelectSchool: (s: CatchmentSchool) => void;
  financialLayer: FinancialLayer;
}) {
  const [tab, setTab] = useState<TabKey>("overview");
  const [hoveredSchool, setHoveredSchool] = useState<string | null>(null);
  const [trendMode, setTrendMode] = useState<"billed" | "collected" | "outstanding" | "breccs">("collected");

  const catchmentSchools = useMemo(() => schools.filter(s => s.catchment_id === catchment.id), [schools, catchment.id]);
  const finance = useMemo(() => getCatchmentFinance(catchmentSchools), [catchmentSchools]);
  const healthScore = useMemo(() => getCatchmentHealthScore(catchmentSchools, finance), [catchmentSchools, finance]);
  const dataQuality = useMemo(() => getDataQualityScore(catchmentSchools), [catchmentSchools]);
  const infra = useMemo(() => getInfrastructureScore(catchmentSchools), [catchmentSchools]);
  const trend = useMemo(() => getMonthlyTrend(finance), [finance]);

  const rankedSchools = useMemo(() => {
    return catchmentSchools.map(s => ({ school: s, fin: getSchoolFinance(s) }))
      .sort((a, b) => b.fin.annualFees - a.fin.annualFees);
  }, [catchmentSchools]);

  const gpsVerified = catchmentSchools.filter(s => s.location_status === "verified").length;
  const needsVerification = catchmentSchools.filter(s => s.location_status === "needs_verification" || s.location_status === "approximate").length;

  const tabs: { key: TabKey; label: string }[] = [
    { key: "overview", label: "Overview" },
    { key: "schools", label: "Schools" },
    { key: "finance", label: "Finance" },
    { key: "infrastructure", label: "Infrastructure" },
    { key: "coverage", label: "Coverage" },
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] flex justify-end"
        style={{ background: "rgba(0,0,0,0.45)" }}
        onClick={onClose}
      >
        <motion.div
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "spring", damping: 30, stiffness: 300 }}
          className="relative h-full w-full max-w-[640px] overflow-y-auto bg-white shadow-2xl"
          onClick={e => e.stopPropagation()}
        >
          {/* ── Hero header ── */}
          <div className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${C.forestDeep}, ${C.forest})` }}>
            <div className="absolute inset-0 opacity-[0.04]" style={{
              backgroundImage: `radial-gradient(circle at 30% 50%, ${C.forestMid} 0%, transparent 50%), radial-gradient(circle at 70% 80%, ${C.emerald} 0%, transparent 40%)`,
            }} />
            <div className="absolute right-0 top-0 h-40 w-40 opacity-[0.06]">
              <Globe2 className="h-full w-full text-white" />
            </div>
            <div className="relative p-6 pb-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/40">
                    <span>Catchment</span>
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.7)" }}>DEMO</span>
                  </div>
                  <h2 className="mt-1 text-2xl font-bold tracking-tight text-white">{catchment.name}</h2>
                  <div className="mt-1 flex items-center gap-1.5 text-xs text-white/50">
                    <MapPin className="h-3 w-3" />
                    {catchment.district ?? "—"} · {catchment.region ?? "—"} · Zimbabwe
                  </div>
                </div>
                <button onClick={onClose} className="rounded-lg p-2 transition hover:bg-white/10">
                  <X className="h-5 w-5 text-white/60" />
                </button>
              </div>

              {/* Breadcrumb */}
              <div className="mt-3 flex items-center gap-1 text-[11px] text-white/40">
                <span>Zimbabwe</span><ChevronRight className="h-3 w-3" />
                <span>{catchment.region ?? "—"}</span><ChevronRight className="h-3 w-3" />
                <span>{catchment.district ?? "—"}</span><ChevronRight className="h-3 w-3" />
                <span className="text-white/70">{catchment.name}</span>
              </div>

              {/* Hero stats */}
              <div className="mt-5 grid grid-cols-3 gap-4">
                <div>
                  <p className="text-3xl font-bold text-white">{fmtNum(catchmentSchools.length)}</p>
                  <p className="text-[10px] uppercase tracking-widest text-white/40">Schools</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-white">{fmtNum(finance.annualFees > 0 ? catchmentSchools.reduce((a, s) => a + s.student_count, 0) : 0)}</p>
                  <p className="text-[10px] uppercase tracking-widest text-white/40">Students</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-white">{fmtUSD(finance.annualFees)}</p>
                  <p className="text-[10px] uppercase tracking-widest text-white/40">School Economy</p>
                </div>
              </div>
            </div>
          </div>

          {/* ── Tabs ── */}
          <div className="sticky top-0 z-10 flex items-center gap-1 border-b bg-white/95 px-4 py-2 backdrop-blur" style={{ borderColor: C.border }}>
            {tabs.map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className="rounded-lg px-3 py-1.5 text-xs font-medium transition"
                style={{
                  background: tab === t.key ? `${C.forest}10` : "transparent",
                  color: tab === t.key ? C.forest : C.textMuted,
                }}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="p-5">
            {/* ── Overview tab ── */}
            {tab === "overview" && (
              <div className="space-y-5">
                {/* KPI strip */}
                <div className="grid grid-cols-3 gap-2">
                  <KpiCard icon={SchoolIcon} label="Schools" value={fmtNum(catchmentSchools.length)} accent={C.forest} />
                  <KpiCard icon={Users} label="Students" value={fmtNum(catchmentSchools.reduce((a, s) => a + s.student_count, 0))} accent={C.emerald} />
                  <KpiCard icon={GraduationCap} label="Teachers" value={fmtNum(catchmentSchools.reduce((a, s) => a + s.teacher_count, 0))} accent={C.sky} />
                  <KpiCard icon={TrendingUp} label="Annual Fees" value={fmtUSD(finance.annualFees)} accent={C.forest} />
                  <KpiCard icon={CheckCircle2} label="Collected" value={fmtUSD(finance.collected)} sub={fmtPct(finance.collectionRate)} accent={C.emerald} />
                  <KpiCard icon={AlertTriangle} label="Outstanding" value={fmtUSD(finance.outstanding)} accent={C.rose} />
                </div>

                {/* Collection ring + health */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col items-center rounded-xl border p-4" style={{ borderColor: C.border }}>
                    <p className="mb-3 text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>Collection Rate</p>
                    <CollectionRing rate={finance.collectionRate} />
                    <div className="mt-3 w-full space-y-1 text-xs">
                      <div className="flex justify-between"><span style={{ color: C.textMuted }}>Collected</span><span className="font-semibold" style={{ color: C.emerald }}>{fmtUSD(finance.collected)}</span></div>
                      <div className="flex justify-between"><span style={{ color: C.textMuted }}>Outstanding</span><span className="font-semibold" style={{ color: C.rose }}>{fmtUSD(finance.outstanding)}</span></div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="rounded-xl border p-4" style={{ borderColor: C.border }}>
                      <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>Catchment Health</p>
                      <div className="mt-2 flex items-end gap-2">
                        <span className="text-3xl font-bold" style={{ color: healthScore > 75 ? C.emerald : healthScore > 50 ? C.sky : C.amber }}>{healthScore}</span>
                        <span className="mb-1 text-xs" style={{ color: C.textMuted }}>{healthScore > 75 ? "Strong" : healthScore > 50 ? "Moderate" : "Needs attention"}</span>
                      </div>
                      <p className="mt-1 text-[10px]" style={{ color: C.textLight }}>Based on coverage, collection, penetration & metadata</p>
                    </div>
                    <div className="rounded-xl border p-4" style={{ borderColor: C.border }}>
                      <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>Data Quality</p>
                      <div className="mt-2 flex items-end gap-2">
                        <span className="text-3xl font-bold" style={{ color: dataQuality > 80 ? C.emerald : dataQuality > 60 ? C.sky : C.amber }}>{fmtPct(dataQuality / 100)}</span>
                      </div>
                      <p className="mt-1 text-[10px]" style={{ color: C.textLight }}>GPS, catchment, geometry & metadata completeness</p>
                    </div>
                  </div>
                </div>

                {/* Financial flow */}
                <div className="rounded-xl border p-4" style={{ borderColor: C.border }}>
                  <p className="mb-3 text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>Financial Flow</p>
                  <div className="flex items-center justify-between">
                    <div className="text-center">
                      <p className="text-[10px] uppercase tracking-wider" style={{ color: C.textMuted }}>Billed</p>
                      <p className="text-lg font-bold" style={{ color: C.forest }}>{fmtUSD(finance.annualFees)}</p>
                    </div>
                    <ChevronRight className="h-5 w-5" style={{ color: C.textLight }} />
                    <div className="text-center">
                      <p className="text-[10px] uppercase tracking-wider" style={{ color: C.textMuted }}>Collected</p>
                      <p className="text-lg font-bold" style={{ color: C.emerald }}>{fmtUSD(finance.collected)}</p>
                    </div>
                    <ChevronRight className="h-5 w-5" style={{ color: C.textLight }} />
                    <div className="text-center">
                      <p className="text-[10px] uppercase tracking-wider" style={{ color: C.textMuted }}>Outstanding</p>
                      <p className="text-lg font-bold" style={{ color: C.rose }}>{fmtUSD(finance.outstanding)}</p>
                    </div>
                  </div>
                </div>

                {/* Opportunity */}
                {finance.unservedSchools > 0 && (
                  <div className="rounded-xl border p-4" style={{ borderColor: `${C.amber}40`, background: `${C.amber}08` }}>
                    <div className="flex items-center gap-2">
                      <Activity className="h-4 w-4" style={{ color: C.amber }} />
                      <p className="text-xs font-bold uppercase tracking-wider" style={{ color: C.amber }}>BRECCS Opportunity</p>
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: `${C.amber}15`, color: C.amber }}>DEMO</span>
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-3 text-center">
                      <div><p className="text-xl font-bold" style={{ color: C.text }}>{finance.unservedSchools}</p><p className="text-[10px]" style={{ color: C.textMuted }}>Unserved Schools</p></div>
                      <div><p className="text-xl font-bold" style={{ color: C.text }}>{fmtNum(finance.unservedStudents)}</p><p className="text-[10px]" style={{ color: C.textMuted }}>Unserved Students</p></div>
                      <div><p className="text-xl font-bold" style={{ color: C.amber }}>{fmtUSD(finance.opportunity)}</p><p className="text-[10px]" style={{ color: C.textMuted }}>Potential Volume</p></div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Schools tab ── */}
            {tab === "schools" && (
              <div className="space-y-2">
                {catchmentSchools.length === 0 ? (
                  <div className="rounded-xl border border-dashed p-8 text-center" style={{ borderColor: C.border }}>
                    <SchoolIcon className="mx-auto mb-2 h-8 w-8" style={{ color: C.textLight }} />
                    <p className="text-sm font-medium" style={{ color: C.text }}>No schools mapped in this catchment</p>
                    <p className="text-xs" style={{ color: C.textMuted }}>No schools are currently assigned to this catchment area.</p>
                  </div>
                ) : (
                  <>
                    <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>{catchmentSchools.length} schools in this catchment</p>
                    {rankedSchools.map(({ school, fin }) => (
                      <SchoolRow
                        key={school.id}
                        school={school}
                        finance={fin}
                        selected={false}
                        hovered={hoveredSchool === school.id}
                        onSelect={() => onSelectSchool(school)}
                        onHover={setHoveredSchool}
                      />
                    ))}
                  </>
                )}
              </div>
            )}

            {/* ── Finance tab ── */}
            {tab === "finance" && (
              <div className="space-y-5">
                <div className="flex items-center justify-center">
                  <CollectionRing rate={finance.collectionRate} size={140} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <KpiCard icon={TrendingUp} label="Billed" value={fmtUSD(finance.annualFees)} accent={C.forest} />
                  <KpiCard icon={CheckCircle2} label="Collected" value={fmtUSD(finance.collected)} accent={C.emerald} />
                  <KpiCard icon={AlertTriangle} label="Outstanding" value={fmtUSD(finance.outstanding)} accent={C.rose} />
                  <KpiCard icon={Activity} label="BRECCS Volume" value={fmtUSD(finance.breccsVolume)} sub={fmtPct(finance.breccsPenetration)} accent={C.sky} />
                </div>

                {/* Trend chart */}
                <div className="rounded-xl border p-4" style={{ borderColor: C.border }}>
                  <div className="mb-3 flex items-center justify-between">
                    <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>Financial Performance</p>
                    <div className="flex gap-1">
                      {(["billed", "collected", "outstanding", "breccs"] as const).map(m => (
                        <button
                          key={m}
                          onClick={() => setTrendMode(m)}
                          className="rounded-md px-2 py-1 text-[10px] font-medium capitalize transition"
                          style={{
                            background: trendMode === m ? C.forest : "transparent",
                            color: trendMode === m ? "white" : C.textMuted,
                          }}
                        >
                          {m}
                        </button>
                      ))}
                    </div>
                  </div>
                  <TrendChart data={trend} mode={trendMode} />
                </div>

                {/* School ranking */}
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>Top Schools by Financial Activity</p>
                  <div className="space-y-1.5">
                    {rankedSchools.slice(0, 10).map(({ school, fin }, i) => (
                      <button
                        key={school.id}
                        onClick={() => onSelectSchool(school)}
                        className="flex w-full items-center gap-3 rounded-lg border p-2.5 text-left transition hover:shadow-sm"
                        style={{ borderColor: C.border }}
                      >
                        <span className="w-6 text-center text-sm font-bold" style={{ color: C.textLight }}>{String(i + 1).padStart(2, "0")}</span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium" style={{ color: C.text }}>{school.name}</p>
                          <p className="text-[10px]" style={{ color: C.textMuted }}>{fmtNum(school.student_count)} students · {fmtPct(fin.collectionRate)} collection</p>
                        </div>
                        <span className="text-sm font-bold" style={{ color: C.forest }}>{fmtUSD(fin.annualFees)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── Infrastructure tab ── */}
            {tab === "infrastructure" && (
              <div className="space-y-4">
                <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>Infrastructure Intelligence</p>
                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: `${C.amber}15`, color: C.amber }}>DEMO</span>
                {[
                  { label: "Water", value: infra.water, icon: Droplet, color: C.sky },
                  { label: "Electricity", value: infra.electricity, icon: Zap, color: C.amber },
                  { label: "ICT", value: infra.ict, icon: Wifi, color: C.forest },
                  { label: "Sanitation", value: infra.sanitation, icon: ShieldCheck, color: C.emerald },
                  { label: "Classroom Capacity", value: infra.classroom, icon: Building2, color: C.forestMid },
                ].map(item => (
                  <div key={item.label} className="rounded-xl border p-3" style={{ borderColor: C.border }}>
                    <div className="mb-2 flex items-center gap-2">
                      <item.icon className="h-4 w-4" style={{ color: item.color }} />
                      <span className="text-sm font-medium" style={{ color: C.text }}>{item.label}</span>
                      <span className="ml-auto text-sm font-bold" style={{ color: item.value > 75 ? C.emerald : item.value > 50 ? C.sky : C.amber }}>{item.value}%</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full" style={{ background: C.border }}>
                      <motion.div
                        className="h-full rounded-full"
                        style={{ background: item.color }}
                        initial={{ width: 0 }}
                        animate={{ width: `${item.value}%` }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── Coverage tab ── */}
            {tab === "coverage" && (
              <div className="space-y-4">
                <div className="rounded-xl border p-4" style={{ borderColor: C.border }}>
                  <p className="mb-3 text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>Catchment Coverage</p>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div><p className="text-2xl font-bold" style={{ color: C.text }}>{catchmentSchools.length}</p><p className="text-[10px]" style={{ color: C.textMuted }}>Total Schools</p></div>
                    <div><p className="text-2xl font-bold" style={{ color: C.emerald }}>{gpsVerified}</p><p className="text-[10px]" style={{ color: C.textMuted }}>GPS Verified</p></div>
                    <div><p className="text-2xl font-bold" style={{ color: needsVerification > 0 ? C.amber : C.emerald }}>{needsVerification}</p><p className="text-[10px]" style={{ color: C.textMuted }}>Needs Verification</p></div>
                  </div>
                </div>

                {needsVerification > 0 && (
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider" style={{ color: C.amber }}>Schools Needing Verification</p>
                    <div className="space-y-1.5">
                      {catchmentSchools.filter(s => s.location_status === "needs_verification" || s.location_status === "approximate").map(s => (
                        <button
                          key={s.id}
                          onClick={() => onSelectSchool(s)}
                          className="flex w-full items-center gap-2 rounded-lg border p-2 text-left transition hover:shadow-sm"
                          style={{ borderColor: `${C.amber}40`, background: `${C.amber}05` }}
                        >
                          <AlertTriangle className="h-3.5 w-3.5" style={{ color: C.amber }} />
                          <span className="flex-1 truncate text-sm" style={{ color: C.text }}>{s.name}</span>
                          <span className="text-[10px] capitalize" style={{ color: C.textMuted }}>{s.location_status}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="rounded-xl border p-4" style={{ borderColor: C.border }}>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>Geographic Details</p>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between"><span style={{ color: C.textMuted }}>Area</span><span className="font-medium" style={{ color: C.text }}>{fmtArea(catchment.area_km2)}</span></div>
                    <div className="flex justify-between"><span style={{ color: C.textMuted }}>County</span><span className="font-medium" style={{ color: C.text }}>{catchment.county ?? "—"}</span></div>
                    <div className="flex justify-between"><span style={{ color: C.textMuted }}>Subcounty</span><span className="font-medium" style={{ color: C.text }}>{catchment.subcounty ?? "—"}</span></div>
                    <div className="flex justify-between"><span style={{ color: C.textMuted }}>Parish</span><span className="font-medium" style={{ color: C.text }}>{catchment.parish ?? "—"}</span></div>
                    <div className="flex justify-between"><span style={{ color: C.textMuted }}>Status</span><span style={{ color: catchment.status === "active" ? C.emerald : C.amber }}>{catchment.status}</span></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Footer actions ── */}
          <div className="sticky bottom-0 flex items-center gap-2 border-t bg-white px-5 py-3" style={{ borderColor: C.border }}>
            <button className="flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium transition hover:bg-gray-50" style={{ borderColor: C.border, color: C.text }}>
              <Maximize2 className="h-3.5 w-3.5" /> Zoom to Catchment
            </button>
            <button className="flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium transition hover:bg-gray-50" style={{ borderColor: C.border, color: C.text }}>
              <Download className="h-3.5 w-3.5" /> Export Report
            </button>
            <div className="ml-auto text-[10px]" style={{ color: C.textLight }}>
              <span className="px-1.5 py-0.5 rounded font-bold" style={{ background: `${C.amber}15`, color: C.amber }}>DEMO DATA</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
